// src/components/TabelaAnalise.jsx
import React, { useMemo } from 'react';
import dayjs from 'dayjs';
import {
  Table, TableHead, TableBody, TableRow, TableCell,
  Paper, Typography, TableContainer
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';

// ===== Células utilitárias =====
const CenteredCell = styled(TableCell)({
  textAlign: 'center',
  fontWeight: 500,
  whiteSpace: 'nowrap'
});

// (igual à versão estável: não tem shouldForwardProp; funciona bem)
const ConditionalCell = styled(CenteredCell)(({ theme, valor }) => ({
  color: valor > 0 ? '#FF0000' : valor < 0 ? '#00B159' : theme.palette.text.primary
}));

// helper numérico
const N = (v) => (v == null || v === '' || isNaN(Number(v)) ? 0 : Number(v));

export default function TabelaAnalise({ rows = [], kmAcopTotal = 0, kmAcopPorMes = {}, onSelectGroup, selectedGroup }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // ==== agregação por GR_ANALISE (mesma lógica da versão estável, com custoRemuImmu) ====
  const dadosPorGrupo = useMemo(() => {
    if (!rows) return [];

    const agrupado = {};

    rows.forEach((row) => {
      const grupo = row.GR_ANALISE || 'Não informado';
      if (!agrupado[grupo]) {
        agrupado[grupo] = {
          gastoProgramado: 0,
          totalRealizado: 0,
          immuPorMes: {} // { 1: somaImmuMes, 2: somaImmuMes, ... }
        };
      }
      agrupado[grupo].gastoProgramado += N(row.META);
      agrupado[grupo].totalRealizado += N(row.GASTO_REAL);
      const d = dayjs(row.DATA);
      if (d.isValid()) {
        const mes = d.month() + 1; // 1..12
        agrupado[grupo].immuPorMes[mes] = (agrupado[grupo].immuPorMes[mes] || 0) + N(row.IMMU);
      }
    });

    return Object.entries(agrupado).map(([grupo, valores]) => {
      const { gastoProgramado, totalRealizado, immuPorMes } = valores;

      const custoEmpresa = kmAcopTotal > 0 ? totalRealizado / kmAcopTotal : 0;

      // ===== IMMU ponderado por KM (mesma metodologia do card) =====
      const numerador = Object.entries(immuPorMes).reduce((sum, [mesStr, immuMes]) => {
        const mes = Number(mesStr);
        const kmMes = N(kmAcopPorMes?.[mes]);
        return sum + (N(immuMes) * kmMes);
      }, 0);

      const totalImmu = kmAcopTotal > 0 ? (numerador / kmAcopTotal) : 0; // R$/km ponderado
      const custoRemuImmu = numerador; // Σ(IMMU*KM)

      // DIFERENÇA correta: REALIZADO - IMMU (R$)
      const diferenca = totalRealizado - custoRemuImmu;

      // % em relação ao IMMU (R$)
      const diferencaPercentual = custoRemuImmu > 0 ? (diferenca / custoRemuImmu) * 100 : 0;      
      const difEmpresaImmu = custoEmpresa - totalImmu;
      const difEmpresaImmuPercent = totalImmu > 0 ? (difEmpresaImmu / totalImmu) * 100 : 0;

      return {
        grupo,
        gastoProgramado,
        totalRealizado,
        diferenca,
        diferencaPercentual,
        totalImmu,
        custoRemuImmu,
        custoEmpresa,
        difEmpresaImmu,
        difEmpresaImmuPercent
      };
    });
  }, [rows, kmAcopTotal, kmAcopPorMes]);

  return (
    <TableContainer component={Paper} sx={{ mt: 4, borderRadius: 3, boxShadow: 4 }}>
      {/* título DENTRO do TableContainer — igual à versão estável */}
      <Typography
        variant="h6"
        align="center"
        sx={{ px: 2, py: 2, fontWeight: 'bold' }}
      >
        ANÁLISE DE GASTOS POR GRUPO
      </Typography>

      {/* sem key extra aqui — igual ao estável */}
      <Table>
        <TableHead>
          <TableRow
            sx={{
              backgroundColor: isDark ? '#2B3242' : '#f5f5f5',
              '& > th': { color: isDark ? theme.palette.grey[100] : 'inherit' }
            }}
          >
            <TableCell sx={{ fontWeight: 'bold' }}>GRUPO</TableCell>
            <CenteredCell sx={{ fontWeight: 'bold' }}>IMMU</CenteredCell>
            <CenteredCell sx={{ fontWeight: 'bold' }}>PROGRAMADO</CenteredCell>
            <CenteredCell sx={{ fontWeight: 'bold' }}>REALIZADO</CenteredCell>
            <CenteredCell sx={{ fontWeight: 'bold' }}>DIFERENÇA</CenteredCell>
            <CenteredCell sx={{ fontWeight: 'bold' }}>DIFERENÇA (%)</CenteredCell>
            <CenteredCell sx={{ fontWeight: 'bold' }}>CUSTO IMMU</CenteredCell>
            <CenteredCell sx={{ fontWeight: 'bold' }}>CUSTO EMPRESA</CenteredCell>
            <CenteredCell sx={{ fontWeight: 'bold' }}>DIF. EMPRESA VS IMMU</CenteredCell>
            <CenteredCell sx={{ fontWeight: 'bold' }}>DIF. EMPRESA VS IMMU (%)</CenteredCell>
          </TableRow>
        </TableHead>

        {/* ÚNICA key: no TableBody — como na versão que não quebrava */}
        <TableBody key={selectedGroup || 'none'}>
          {dadosPorGrupo.map((item, idx) => {
            const isSelected = selectedGroup === item.grupo;
            const handleClick = () => onSelectGroup?.(isSelected ? null : item.grupo);

            return (
              <TableRow
                key={`${item.grupo}-${item.gastoProgramado}-${item.totalRealizado}`} // mesmo padrão estável
                onClick={handleClick}
                hover
                sx={{
                  cursor: 'pointer',
                  '&:hover td': { backgroundColor: 'rgba(14,165,233,0.08)' },
                  '& td': { transition: 'background-color .2s ease' },
                  ...(isSelected && { '& td': { backgroundColor: 'rgba(14,165,233,0.12)' } })
                }}
              >
                <TableCell>
                  <span style={{ fontWeight: isSelected ? 700 : 500 }}>{item.grupo}</span>
                </TableCell>

                {/* nova coluna após GRUPO */}
                <CenteredCell>
                  {item.custoRemuImmu.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                </CenteredCell>

                <CenteredCell>
                  {item.gastoProgramado.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                </CenteredCell>

                <CenteredCell>
                  {item.totalRealizado.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                </CenteredCell>

                <ConditionalCell valor={item.diferenca}>
                  {item.diferenca.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                </ConditionalCell>

                <ConditionalCell valor={item.diferenca}>
                  {item.diferencaPercentual.toFixed(2).replace('.', ',')}%
                </ConditionalCell>

                <CenteredCell>
                  {item.totalImmu.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                </CenteredCell>

                <CenteredCell>
                  {item.custoEmpresa.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                </CenteredCell>

                <ConditionalCell valor={item.difEmpresaImmu}>
                  {item.difEmpresaImmu.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                </ConditionalCell>

                <ConditionalCell valor={item.difEmpresaImmu}>
                  {item.difEmpresaImmuPercent.toFixed(2).replace('.', ',')}%
                </ConditionalCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
